import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, effect, EffectRef, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChatBotPosition, ChatMessage, ReferenceDocument } from '@coin/chat-bot-util-types';
import { AuthService } from '@coin/shared/auth/data-access';
import { ImmerComponentStore } from 'ngrx-immer/component-store';
import { from, switchMap, takeUntil } from 'rxjs';
import { ChatStoreState } from '../interfaces/chat-store-state.interface';
import { ChatService, ResponseChatMessage } from './chat.service';
import { InitialChatStoreState } from './initial-chat-store-state.model';
import { MessageId } from './message-id.enum';

@Injectable()
export class ChatStore extends ImmerComponentStore<ChatStoreState> {
  private authService = inject(AuthService);
  private chatService = inject(ChatService);
  private overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;

  private firstName = computed(() => {
    const isAuthenticated = this.isAuthenticated();
    const user = this.activeUser();

    if (isAuthenticated && user) {
      return user.given_name || user.firstName;
    }

    return null;
  });

  isAuthenticated = toSignal(this.authService.isAuthenticated$);
  activeUser = toSignal(
    this.authService.isAuthenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated ? from(this.authService.getActiveUser()) : from(Promise.resolve(undefined))
      )
    )
  );
  user = computed(() => {
    const isAuthenticated = this.isAuthenticated();
    const user = this.activeUser();

    if (isAuthenticated && user) {
      return {
        gid: user.gid,
        firstName: user.given_name || user.firstName,
        lastName: user.family_name || user.lastName
      };
    }

    return null;
  });

  private maxMesssages = 10;
  isFirstNameAdded = false;
  nameEffect: EffectRef | null = null;

  isSendDisabled = computed(() => {
    const messages = this.getMessages()();
    const isThereAnyPendingResponse = messages.some(message => message.id === MessageId.PENDING_RESPONSE);
    const isThereAnyAssistantThinkingMessage = messages.some(message => message.id === MessageId.ASSISTANT_THINKING);
    const chatLoading = this.isLoading()(); // if the chat is loading

    return (
      isThereAnyPendingResponse ||
      isThereAnyAssistantThinkingMessage || // if there is any assistant thinking message
      chatLoading
    );
  });

  constructor() {
    super(InitialChatStoreState);

    // add effect to listen for first name changes
    this.nameEffect = effect(() => {
      const firstName = this.firstName();
      if (!this.isFirstNameAdded && firstName) {
        const initMessage = this.getInitialMessage();
        this.setState(state => ({
          ...state,
          _messages: [
            ...state._messages.map(m => {
              if (m.id === MessageId.INITIAL_MESSAGE) {
                return initMessage;
              }
              return m;
            })
          ]
        }));

        // / terminate the effect
        this.isFirstNameAdded = true;
        this.nameEffect?.destroy();
      }
    });

    // check if messages are over the limit then clean up
    effect(() => {
      const messages = this.getMessages()();
      if (messages.length > this.maxMesssages) {
        this.setState(state => ({
          ...state,

          _messages: state._messages.slice(0, this.maxMesssages)
        }));
      }
    });
  }

  setChatDialogComponent(componentPortal: ComponentPortal<unknown>) {
    this.setState(draft => {
      draft._componentPortal = componentPortal;
    });
  }

  setError(error: string | null) {
    this.setState(draft => {
      draft._error = error;
    });
  }

  getError() {
    return this.selectSignal(state => state._error);
  }

  getMessages() {
    return this.selectSignal(state => state._messages);
  }

  postUserMessage(content: string) {
    const userMessage = this.createUserMessage(content);
    this.setState(state => {
      return {
        ...state,
        _messages: [userMessage, ...state._messages],
        _isLoading: true,
        _lastUpdatedAt: new Date()
      };
    });
    this.addAssistantIsThinkingMessage();
    this.chatService
      .streamChat(this.getPreviousContent())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (message: ResponseChatMessage) => {
          const hasPendingResponse = this.hasPendingResponse();
          if (!hasPendingResponse) {
            const botMessage: ChatMessage = {
              id: MessageId.PENDING_RESPONSE,
              content: message.data,
              owner: 'assistant',
              type: 'text',
              finished: false,
              createdAt: new Date(),
              referenceDocuments: message.referenceDocs || []
            };
            this.removeAssistantIsThinkingMessage();
            this.setState(state => {
              return {
                ...state,
                _messages: [botMessage, ...state._messages],
                _isLoading: false,
                _lastUpdatedAt: new Date()
              };
            });
          } else {
            const pendingMessage = this.getMessages()().find(m => m.id === MessageId.PENDING_RESPONSE);
            if (pendingMessage) {
              this.setState(state => {
                return {
                  ...state,
                  _messages: [
                    {
                      ...pendingMessage,
                      content: pendingMessage.content + message.data,
                      finished: false,
                      referenceDocuments: [...pendingMessage.referenceDocuments, ...(message.referenceDocs || [])]
                    },
                    ...state._messages.filter(m => m.id !== MessageId.PENDING_RESPONSE)
                  ]
                };
              });
            }
          }
        },
        complete: () => {
          const pendingResponseMessage = this.getMessages()().find(m => m.id === MessageId.PENDING_RESPONSE);
          if (pendingResponseMessage) {
            this.setState(state => {
              return {
                ...state,
                _messages: [
                  {
                    ...pendingResponseMessage,
                    id: new Date().getTime().toString(),
                    finished: true
                  },
                  ...state._messages.filter(m => m.id !== MessageId.PENDING_RESPONSE)
                ]
              };
            });
          } else {
            // find assistant thinking message and remove it
            this.removeAssistantIsThinkingMessage();
            // add a new message from assistant stating that it had issue with response
            const errorMessage: ChatMessage = {
              id: new Date().getTime().toString(),
              content: 'I had an issue processing your request. Please try again later.',
              owner: 'assistant',
              type: 'text',
              finished: true,
              createdAt: new Date(),
              referenceDocuments: []
            };
            this.setState(state => {
              return {
                ...state,
                _messages: [errorMessage, ...state._messages],
                _isLoading: false,
                _lastUpdatedAt: new Date()
              };
            });
            console.error('No pending response message found after streaming complete.');
          }
        },
        error: (error: Error) => {
          console.error('Error while streaming chat response:', error);
          this.setError(error.message);
          this.removeAssistantIsThinkingMessage();
        }
      });
  }

  isLoading() {
    return this.selectSignal(state => state._isLoading);
  }

  getLastUpdatedAt() {
    return this.selectSignal(state => state._lastUpdatedAt);
  }

  isOpen() {
    return this.selectSignal(state => state._isOpen);
  }

  setOpen(isOpen: boolean) {
    this.setState(draft => {
      draft._isOpen = isOpen;
    });
  }

  setPosition(position: ChatBotPosition) {
    this.patchState(state => ({
      ...state,
      _currentPosition: position
    }));
  }

  downloadFile(document: ReferenceDocument) {
    this.chatService.downloadReferenceDocument(document);
  }

  clearMessages() {
    this.setState(state => {
      return {
        ...state,
        _messages: [],
        _lastUpdatedAt: new Date()
      };
    });
  }

  toggle() {
    const componentPortal = this.getComponentPortal();

    if (!componentPortal) {
      console.error('No component portal was set');
      return;
    }

    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef = null;
      this.setOpen(false);
      return;
    }
    let positionStrategy = this.overlay.position().global().bottom('0.25rem');

    positionStrategy = positionStrategy.right('0.25rem');
    positionStrategy = positionStrategy.top('0.25rem');

    const config: OverlayConfig = {
      hasBackdrop: false,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'chatbot-overlay-panel'
    };

    this.overlayRef = this.overlay.create(config);
    this.overlayRef.attach(componentPortal);
    this.setOpen(true);
  }

  private createUserMessage(content: string): ChatMessage {
    return {
      id: new Date().getTime().toString(),
      content,
      owner: 'user',
      type: 'text',
      finished: true,
      createdAt: new Date(),
      referenceDocuments: []
    };
  }

  private getComponentPortal() {
    return this.get(state => state._componentPortal);
  }

  private getInitialMessage() {
    const initialMessage: ChatMessage = {
      id: MessageId.INITIAL_MESSAGE,
      content: `Hello${this.firstName() ? ` ${this.firstName()}` : ''}, how can I help you today?`,
      owner: 'assistant',
      type: 'text',
      finished: true,
      createdAt: new Date(),
      referenceDocuments: []
    };

    return initialMessage;
  }

  private addAssistantIsThinkingMessage(): void {
    const message: ChatMessage = {
      id: MessageId.ASSISTANT_THINKING,
      content: 'Assistant is thinking...',
      owner: 'assistant',
      type: 'text',
      finished: false,
      createdAt: new Date(),
      referenceDocuments: []
    };

    this.setState(state => {
      return {
        ...state,
        _messages: [message, ...state._messages]
      };
    });
  }

  private removeAssistantIsThinkingMessage(): void {
    this.setState(state => {
      return {
        ...state,
        _messages: state._messages.filter(message => message.id !== MessageId.ASSISTANT_THINKING)
      };
    });
  }

  private hasPendingResponse() {
    return this.get(state => state._messages.some(message => message.id === MessageId.PENDING_RESPONSE));
  }

  private getPreviousContent() {
    const messages = this.getMessages()();
    return `${messages
      .filter(
        message =>
          message.id !== MessageId.PENDING_RESPONSE &&
          message.id !== MessageId.ASSISTANT_THINKING &&
          message.id !== MessageId.INITIAL_MESSAGE
      )
      .map(message => message.content)
      .reverse()
      .join('\n')}`;
  }
}
